import { useState, useEffect, useCallback } from "react";
import {Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails} from "@mui/material";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";

import ExpandMore from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import userService from "@plannel/services/system/user-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import "@plannel/scss/components/DataLoadValidation.scss"
import debug from "@plannel/utils/debug-util";
import { withTranslation } from "react-i18next";
import {includes, isEmpty, isEqual, keys, size, values} from "lodash";
import Snackbar from "@plannel/components/Snackbar";
import ModuleVersionSetting from "@plannel/components/ModuleVersionSetting";
import CodeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";

const MP_SETTINGS_CONFIG_CD = "MP_SETTINGS_CONFIG";
const DATE_PRIORITY_CD = "DATE_PRIORITY";
const DEMAND_PRIORITY_CD = "DEMAND_PRIORITY";

// 수요 우선순위 Others — saas-ai(AiMpScenarioPlanning) 드래그&드롭 빌더 스타일. accent=앱 브랜드 teal.
const ACC = "#048BA8";
const NEUTRAL_BORDER = "#E0E0E0";
const POOL_BG = "#F1F4F8";
const DESC_RED = "#E53935";

const MpSettings = ({ t }) => {
  let user;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    debug(e);
    user = {};
  }
  
  const { DATE_PRIORITY: { DUE_DATE, W_DUE_DATE, M_DUE_DATE, PLAN_HORIZON }, DEMAND_PRIORITY } = CodeUtil.priorityItems;
  const { MP } = CodeUtil.module;
  const MP_CONFIG_PARAMS = { moduleCd: MP, activeFlg: true, configCd: MP_SETTINGS_CONFIG_CD };
  const DEMAND_PRIORITY_PARAMS = { moduleCd: MP, activeFlg: true, configCd: DEMAND_PRIORITY_CD };

  const [userRoles, setUserRoles] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [mpPlanConfig,setMpPlanConfig] = useState(MP_CONFIG_PARAMS);
  const [objVal, setObjVal] = useState(null);
  // 서버에서 config 를 새로 받은 순간에만 증가한다 — ModuleVersionSetting 의 재마운트 트리거.
  // objVal 자체를 key 로 쓰면 편집 한 글자마다 재마운트되어 입력 포커스가 날아간다.
  const [configLoadKey, setConfigLoadKey] = useState(0);

  // Demand priority (saas-ai 스타일 드래그&드롭 빌더)
  const [planConfig, setPlanConfig] = useState(DEMAND_PRIORITY_PARAMS);
  const [datePriority, setDatePriority] = useState(DUE_DATE);
  const [demandPriority, setDemandPriority] = useState([]);
  const [appliedItems, setAppliedItems] = useState([]); // 적용(우선순위 순)
  const [dndOver, setDndOver] = useState(null);          // 드롭 하이라이트: "pool" | 행 index

  // Date 우선순위 옵션 (칩 + Appliance 0번 고정행 공용)
  const datePriorityOptions = [
    { value: DUE_DATE, label: t("dueDate") },
    { value: W_DUE_DATE, label: t("weekOfDueDate") },
    { value: M_DUE_DATE, label: t("monthOfDueDate") },
    { value: PLAN_HORIZON, label: t("planHorizon") },
  ];
  const selectedDateLabel = datePriorityOptions.find(o => o.value === datePriority)?.label ?? "";

  const getGridData = data => {
    const gridData = [];
    if (data) {
      const priorityNms = keys(data);
      for (const priorityNm of priorityNms) {
        const seq = data[priorityNm];
        if (seq !== 99) {
          gridData.push({
            priorityItem : DEMAND_PRIORITY?.[priorityNm]?.name,
            sorting: DEMAND_PRIORITY?.[priorityNm]?.sorting,
            seq,
          })
        }
      }
    }

    return gridData;
  }

    const retrieveUserRoles = () => {
      userService.getUserAccessRoles({ username: user?.username })
        .then(
          (response) => {
            const userAccess = response.data;
            setUserRoles(userAccess?.roles);
          },
        )
        .catch(e => { debug(e); });
    };

  const retrievePlanConfig = () => {
    planConfigService.getConfigCds([DEMAND_PRIORITY_PARAMS, MP_CONFIG_PARAMS])
      .then((response) => {
        const results = response?.data;
        for (const result of results) {
          if (result?.configCd === DEMAND_PRIORITY_CD) {
            setPlanConfig(result);
            const resData = result?.objVal;
            if (resData?.DEMAND_PRIORITY) {
              setDemandPriority(getGridData(resData?.DEMAND_PRIORITY));
            }
            if (resData?.DATE_PRIORITY) {
              setDatePriority(resData?.DATE_PRIORITY);
            }
          } else if (result?.configCd === MP_SETTINGS_CONFIG_CD) {
            setMpPlanConfig(result);
            setObjVal(result?.objVal);
          }
        }

      }).catch(e => {
      debug(e);
    }).finally(() => {
      setConfigLoadKey(prev => prev + 1);
    })
  };

  const defaultPriorityData =
    values(DEMAND_PRIORITY)?.filter(item => !isEqual(item.name, "dueDate")).map(item => {
    return {
      priorityItem: item.name,
      sorting: item.sorting,
      seq: item.seq,
    }
  });

  // 적용에 없는 항목 = 풀(미적용)
  const poolItems = defaultPriorityData.filter(
    it => !appliedItems.some(a => a.priorityItem === it.priorityItem)
  );

  // ── 드래그&드롭 ──
  const itemByName = name => defaultPriorityData.find(it => it.priorityItem === name);
  const isApplied = name => appliedItems.some(it => it.priorityItem === name);
  const dragStart = name => e => {
    e.dataTransfer.setData("text/plain", name);
    e.dataTransfer.effectAllowed = "move";
  };
  const applyAt = (name, idx) => setAppliedItems(prev => {
    const rest = prev.filter(it => it.priorityItem !== name);
    const at = idx == null ? rest.length : Math.max(0, Math.min(idx, rest.length));
    rest.splice(at, 0, prev.find(it => it.priorityItem === name) || itemByName(name));
    return rest;
  });
  const reorder = (name, idx) => setAppliedItems(prev => {
    const cur = prev.findIndex(it => it.priorityItem === name);
    if (cur < 0) return prev;
    const item = prev[cur];
    const rest = prev.filter(it => it.priorityItem !== name);
    let at = idx == null ? rest.length : (idx > cur ? idx - 1 : idx);
    at = Math.max(0, Math.min(at, rest.length));
    rest.splice(at, 0, item);
    return rest;
  });
  const dropAt = idx => e => {
    e.preventDefault();
    setDndOver(null);
    const name = e.dataTransfer.getData("text/plain");
    if (!name) return;
    if (isApplied(name)) reorder(name, idx); else applyAt(name, idx);
  };
  const removeApplied = name => setAppliedItems(prev => prev.filter(it => it.priorityItem !== name));
  const dropOnPool = e => {
    e.preventDefault();
    setDndOver(null);
    const name = e.dataTransfer.getData("text/plain");
    if (name && isApplied(name)) removeApplied(name);
  };

  const poolChipSx = {
    display: "inline-flex", alignItems: "center", gap: 0.4,
    border: `1px solid ${NEUTRAL_BORDER}`, borderRadius: "7px", px: "8px", py: "3px",
    fontSize: FS.SUB, fontWeight: 700, color: "text.secondary", bgcolor: "#fff", cursor: "grab",
    transition: "background-color .12s, box-shadow .12s, border-color .12s",
    "&:hover": { bgcolor: `${ACC}0D`, borderColor: ACC, boxShadow: `0 0 0 1px ${ACC}` },
  };
  const dirArrow = sorting => (
    <Box component="span" sx={{ fontWeight: 700, lineHeight: 1, flexShrink: 0, color: sorting === "asc" ? ACC : DESC_RED }}>
      {sorting === "asc" ? "▲" : "▼"}
    </Box>
  );

  const getSavePriorityData = () => {
    const finalAppliedObj = {};
    const finalDemandPriority = {};
    let priSeq = 1;
    let isChanged = false;

    appliedItems.forEach(item => {
      for (const demandPriKey of keys(DEMAND_PRIORITY)) {
        if (isEqual(DEMAND_PRIORITY[demandPriKey].name, item.priorityItem)) {
          finalDemandPriority[demandPriKey] = priSeq;
          priSeq++;
        }
      }
    });

    const existPriority = keys(finalDemandPriority);
    for (const demandPriKey of keys(DEMAND_PRIORITY)) {
      if (!includes(existPriority, demandPriKey)) {
        finalDemandPriority[demandPriKey] = 99;
      }
    }

    finalAppliedObj[DATE_PRIORITY_CD] = datePriority;
    finalAppliedObj[DEMAND_PRIORITY_CD] = finalDemandPriority;

    if (!isEqual(finalAppliedObj, planConfig?.objVal)) {
      isChanged = true;
    }

    const params = {
      ...planConfig,
      objVal: finalAppliedObj
    };

    return isChanged ? params : null;
  }

  const handleSave = () => {
    const priorityData = getSavePriorityData();
    const planConfigSaveData = { ...mpPlanConfig, objVal };

    const savedData = [planConfigSaveData];
    if (priorityData !== null) {
      savedData.push(priorityData);
    }

    planConfigService.upsert(savedData)
      .then((response) => {
        const { data } = response;
        if (response?.status === 200 && !isEmpty(data)) {
          const rpSettingConfigs = data[0];
          setMpPlanConfig(rpSettingConfigs)
          setObjVal(rpSettingConfigs?.objVal)
          setSnackInfo({ open: true, severity: "success", content: t("MSG_SaveSuccess") });
        } else {
          setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") });
        }
      }).catch((error) => {
      debug(error);
    }).finally(() => {
      retrievePlanConfig();
    })
  }

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      retrievePlanConfig();
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  useEffect(() => {
    retrieveUserRoles();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadApplied = useCallback(() => {
    if (size(demandPriority) > 0) {
      setAppliedItems([...demandPriority].sort((a, b) => (a?.seq < b?.seq ? -1 : 1)));
    } else {
      setAppliedItems([]);
    }
  }, [demandPriority]);

  useEffect(() => {
    loadApplied();
  }, [demandPriority, loadApplied]);

  return (
    <Box id={"data-load-validation"} sx={{ mt: 1, height: "100%", width: "100%" }}>
      <Box sx={{ width: "100%", height: "calc(100% - 8px)", overflow: "auto" }}>
        {/* Version Setting */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("version")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ModuleVersionSetting key={configLoadKey} moduleCd={"MP"} config={objVal} setConfig={setObjVal} userRoles={userRoles} />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Date Priority */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1a-content"
              id="panel1a-header">
              <Typography className="content-title">{t("demandPriority")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2, width: "100%" }}>
                <Typography variant="h6" sx={{ fontSize: FS.DISPLAY }}>{t("date")}</Typography>
                {/* segmented control — 연결된 버튼 하나로 '단일 선택'임을 명확히 (칩 분리형은 다중선택처럼 보임) */}
                <SegmentedControl
                  sx={{ mt: 1 }}
                  value={datePriority}
                  onChange={setDatePriority}
                  options={datePriorityOptions}
                />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontSize: FS.DISPLAY }}>{t("others")}</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, alignItems: "start", maxWidth: 1000, mt: 1, mb: 1 }}>

                  {/* 좌: 미적용 풀 — 드롭 시 적용 해제 */}
                  <Box
                    onDragOver={e => { e.preventDefault(); setDndOver("pool"); }}
                    onDragLeave={() => setDndOver(p => (p === "pool" ? null : p))}
                    onDrop={dropOnPool}
                    sx={{
                      borderRadius: "8px",
                      border: `1px dashed ${dndOver === "pool" ? ACC : NEUTRAL_BORDER}`,
                      bgcolor: dndOver === "pool" ? `${ACC}0D` : POOL_BG,
                      p: "8px 10px", minHeight: 100, boxSizing: "border-box", minWidth: 0,
                    }}>
                    <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                      {t("basicCandidates")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {poolItems.map(o => (
                        <Box key={o.priorityItem} draggable onDragStart={dragStart(o.priorityItem)} sx={poolChipSx}>
                          {t(o.priorityItem)}
                          {dirArrow(o.sorting)}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* 우: 적용(우선순위 순) — 말미 추가 드롭 + 행별 삽입 드롭 */}
                  <Box
                    onDragOver={e => e.preventDefault()}
                    onDrop={dropAt(appliedItems.length)}
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${appliedItems.length ? `${ACC}55` : NEUTRAL_BORDER}`,
                      bgcolor: "#fff", p: "8px 10px", minHeight: 100, boxSizing: "border-box", minWidth: 0,
                    }}>
                    <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: appliedItems.length ? ACC : "text.secondary", mb: 0.75 }}>
                      {t("appliance")}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {/* 0번 고정 행: Date (priority 0, 삭제 불가) */}
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 0.5, width: "100%", boxSizing: "border-box",
                        border: `1px solid ${ACC}`, borderRadius: "7px", px: "8px", py: "4px",
                        bgcolor: `${ACC}24`, color: "text.primary", fontSize: FS.SUB, fontWeight: 700,
                      }}>
                        <Box component="span" sx={{ minWidth: 16, height: 16, px: "3px", borderRadius: "50%", bgcolor: ACC, color: "#fff", fontSize: FS.CAPTION, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>0</Box>
                        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedDateLabel}
                          <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 0.5 }}>({t("date")})</Box>
                        </Box>
                        <LockOutlinedIcon sx={{ fontSize: 14, color: ACC, flexShrink: 0 }} />
                      </Box>
                      {appliedItems.length === 0 ? (
                        <Box sx={{ borderRadius: "6px", border: `1px dashed ${NEUTRAL_BORDER}`, py: "16px" }} />
                      ) : appliedItems.map((item, i) => (
                          <Box
                            key={item.priorityItem}
                            draggable
                            onDragStart={dragStart(item.priorityItem)}
                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDndOver(i); }}
                            onDragLeave={() => setDndOver(p => (p === i ? null : p))}
                            onDrop={e => { e.stopPropagation(); dropAt(i)(e); }}
                            sx={{
                              display: "flex", alignItems: "center", gap: 0.5, width: "100%", boxSizing: "border-box",
                              border: `1px solid ${ACC}`, borderRadius: "7px", px: "8px", py: "4px",
                              bgcolor: `${ACC}14`, color: "text.primary", fontSize: FS.SUB, fontWeight: 700, cursor: "grab",
                              boxShadow: dndOver === i ? `inset 0 2px 0 0 ${ACC}` : "none",
                              transition: "background-color .12s, box-shadow .12s",
                              "&:hover": { bgcolor: `${ACC}2E`, boxShadow: dndOver === i ? `inset 0 2px 0 0 ${ACC}` : `0 0 0 1px ${ACC}` },
                            }}>
                            <Box component="span" sx={{
                              minWidth: 16, height: 16, px: "3px", borderRadius: "50%", bgcolor: ACC, color: "#fff",
                              fontSize: FS.CAPTION, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                              {i + 1}
                            </Box>
                            <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {t(item.priorityItem)}
                            </Box>
                            {dirArrow(item.sorting)}
                            <Box
                              component="button"
                              type="button"
                              onClick={() => removeApplied(item.priorityItem)}
                              sx={{
                                minWidth: 16, height: 16, px: "2px", border: "none", borderRadius: "4px",
                                bgcolor: `${ACC}14`, color: ACC, fontSize: FS.CAPTION, lineHeight: 1, cursor: "pointer", ml: 0.2,
                                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                "&:hover": { bgcolor: `${ACC}26` },
                              }}>
                              ✕
                            </Box>
                          </Box>
                      ))}
                    </Box>
                  </Box>

                </Box>
              </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        <Button
          sx={{ width: "160px", height: "40px", mb: 2 }}
          className="content-button"
          color="primary"
          variant="contained"
          component="span"
          startIcon={<SaveIcon />}
          onClick={handleSave}>
            {t("save")}
        </Button>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box >

  );
};

export default withTranslation()(MpSettings);
